
import { getRequest } from '../../../services/httpService';
import { useQuery } from '@tanstack/react-query';

// ----------------------
// Pharmacy Invoice API Types (Checked In)
// ----------------------

export interface PharmacyInvoiceItem {
  patient_id: number;
  appointment_id: number | null;
  drug_id: number[];
  date: string;                    // YYYY-MM-DD
  patient_name: string;
  age: number | null;
  provider: string | null;
  prescription_status: 'ordered' | 'unordered';
  medical_record_no: string;
  invoice_id: number | string;
  gender: string;
  dispense_number?: string;
}

// ----------------------
// Pharmacy Billed Invoice Types
// ----------------------

export interface PharmacyBilledInvoiceItem {
  id: number;
  patient_id: number;
  date: string;                    // YYYY-MM-DD
  patient_name: string;
  age: number | null;
  patient_mrn: string;
  gender: string;
  bill_no: string;
  total_amt: string | number;
  concession_amt: string | number;
  net_amt: string | number;
  paid_amt: string | number;
  balance_amt: string | number;
  status: 'paid' | 'unpaid' | 'partially_paid';
  bill_remark: string | null;
}

// ----------------------
// Common Response Types
// ----------------------

export interface PharmacyInvoiceMeta {
  total_count: number;
  limit: number;
  offset: number;
  next: string | null;
  previous: string | null;
}

export interface PharmacyInvoiceCounts {
  total: number;
  ordered: number;
  unordered: number;
  all_checked_in: number;
}

export interface PharmacyInvoiceData {
  objects: PharmacyInvoiceItem[];
  meta: PharmacyInvoiceMeta;
  counts: PharmacyInvoiceCounts;
}

export interface PharmacyBilledInvoiceData {
  objects: PharmacyBilledInvoiceItem[];
  meta: PharmacyInvoiceMeta;
  counts: PharmacyInvoiceCounts;
}

export interface PharmacyInvoiceListResponse {
  status: boolean;
  message: string;
  data: PharmacyInvoiceData;
}

export interface PharmacyBilledInvoiceListResponse {
  status: boolean;
  message: string;
  data: PharmacyBilledInvoiceData;
}

export interface PharmacyInvoiceFilters {
  clinic_id?: number;
  date_from?: string;     // YYYY-MM-DD
  date_to?: string;       // YYYY-MM-DD
  patient_id?: number;
  prescription_status?: 'ordered' | 'unordered' | 'dispensed';
  search?: string;
  limit?: number;
  offset?: number;
}

// Billed Invoices API filters
export interface PharmacyBilledInvoiceFilters {
  from_date?: string;     // YYYY-MM-DD
  to_date?: string;       // YYYY-MM-DD
  search?: string;
  patient_id?: number;
  status?: 'paid' | 'unpaid' | 'partially_paid';
  limit?: number;
  offset?: number;
}


// ----------------------
// Pharmacy Invoice API
// ----------------------

/**
 * Converts PharmacyInvoiceFilters to URLSearchParams for API requests.
 */
function pharmacyFiltersToParams(filters: PharmacyInvoiceFilters): URLSearchParams {
  const {
    clinic_id,
    patient_id,
    date_from,
    date_to,
    prescription_status,
    search,
    limit = 10,
    offset = 0,
  } = filters;

  const params = new URLSearchParams();
  if (clinic_id != null) params.set('clinic_id', String(clinic_id));
  if (patient_id != null) params.set('patient_id', String(patient_id));
  if (date_from) params.set('date_from', date_from);
  if (date_to) params.set('date_to', date_to);
  if (prescription_status) params.set('prescription_status', prescription_status);
  if (search) params.set('search', search);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return params;
}

/**
 * Fetches pharmacy invoices from API using provided filters.
 */
export function fetchPharmacyInvoices(filters: PharmacyInvoiceFilters): Promise<PharmacyInvoiceListResponse> {
  const params = pharmacyFiltersToParams(filters);
  return getRequest(`pharmacy-invoice/?${params.toString()}`);
}


// ----------------------
// Pharmacy Billed Invoices API
// ----------------------

/**
 * Converts PharmacyBilledInvoiceFilters to URLSearchParams for API requests.
 */
function billedFiltersToParams(filters: PharmacyBilledInvoiceFilters): URLSearchParams {
  const {
    from_date,
    to_date,
    search,
    patient_id,
    status,
    limit = 10,
    offset = 0,
  } = filters;

  const params = new URLSearchParams();
  if (from_date) params.set('from_date', from_date);
  if (to_date) params.set('to_date', to_date);
  if (search) params.set('search', search);
  if (patient_id != null) params.set('patient_id', String(patient_id));
  if (status) params.set('status', status);
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  return params;
}

/**
 * Fetches billed pharmacy invoices from API using provided filters.
 */
export function fetchPharmacyBilledInvoices(filters: PharmacyBilledInvoiceFilters): Promise<PharmacyBilledInvoiceListResponse> {
  const params = billedFiltersToParams(filters);
  return getRequest(`pharmacy-invoice/billed-invoices/?${params.toString()}`);
}

/**
 * Builds a stable query key for React Query based on billed pharmacy filters.
 */
export const buildPharmacyBilledInvoicesQueryKey = (filters: PharmacyBilledInvoiceFilters) => [
  'pharmacy-billed-invoices',
  { ...filters }
];

/**
 * Builds a stable query key for React Query based on pharmacy filters.
 */
export const buildPharmacyInvoicesQueryKey = (filters: PharmacyInvoiceFilters) => [
  'pharmacy-invoices',
  { ...filters }
];


// ----------------------
// Hooks
// ----------------------

export interface UsePharmacyInvoicesQueryParams extends PharmacyInvoiceFilters {
  enabled?: boolean;
}

/**
 * React Query hook to fetch pharmacy invoices with filters.
 *
 * @example
 * const { data, isLoading, error } = usePharmacyInvoicesQuery({
 *   clinic_id: 7,
 *   date_from: '2025-12-10',
 *   date_to: '2025-12-11',
 *   prescription_status: 'ordered',
 *   search: 'rose',
 *   enabled: !!clinicId
 * });
 */
export function usePharmacyInvoicesQuery(filters: UsePharmacyInvoicesQueryParams) {
  const { enabled = true, ...rest } = filters;
  return useQuery<PharmacyInvoiceListResponse>({
    queryKey: buildPharmacyInvoicesQueryKey(rest),
    enabled,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: () => fetchPharmacyInvoices(rest),
  });
}


export interface UsePharmacyBilledInvoicesQueryParams extends PharmacyBilledInvoiceFilters {
  enabled?: boolean;
}

/**
 * React Query hook to fetch billed pharmacy invoices with filters.
 *
 * @example
 * const { data, isLoading, error } = usePharmacyBilledInvoicesQuery({
 *   from_date: '2025-12-10',
 *   to_date: '2025-12-11',
 *   search: 'Rose Ben',
 *   status: 'paid',
 *   enabled: true
 * });
 */
export function usePharmacyBilledInvoicesQuery(filters: UsePharmacyBilledInvoicesQueryParams) {
  const { enabled = true, ...rest } = filters;
  return useQuery<PharmacyBilledInvoiceListResponse>({
    queryKey: buildPharmacyBilledInvoicesQueryKey(rest),
    enabled,
    staleTime: 30_000,
    gcTime: 300_000,
    retry: 1,
    refetchOnWindowFocus: false,
    queryFn: () => fetchPharmacyBilledInvoices(rest),
  });
}


